import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { baseUrlApi } from '../../utils/url';

interface Rent {
  id: number;
  amount: number;
  fee: number;
  day: number | null;
  start: string;
  start_date?: string;
  end_date: string | null;
  water_charge: number;
  active: boolean;
  LeaseId: number;
  LocalId: number;
  local?: any;
}

interface RentState {
  rents: Rent[];
  rent: Rent | null;
  rentsByLease: Rent[];
  loading: boolean;
  error: string | null;
}

const initialState: RentState = {
  rents: [],
  rent: null,
  rentsByLease: [],
  loading: false,
  error: null,
};

export const fetchRents = createAsyncThunk(
  'rent/fetchAll',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const token = state.authentification.user.token;
      const response = await axios.get(`${baseUrlApi}/rents`, {
        headers: { Authorization: `bearer ${token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch rents');
    }
  }
);

export const fetchRent = createAsyncThunk(
  'rent/fetchOne',
  async (rentId: number, { getState, dispatch, rejectWithValue }) => {
    try {
      const state: any = getState();
      const token = state.authentification.user.token;
      const response = await axios.get(`${baseUrlApi}/rents/${rentId}`, {
        headers: { Authorization: `bearer ${token}` }
      });
      dispatch(fetchRents());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch rent');
    }
  }
);

export const fetchRentByLease = createAsyncThunk(
  'rent/fetchByLease',
  async (leaseId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const token = state.authentification.user.token;
      const response = await axios.get(`${baseUrlApi}/rents/lease/${leaseId}`, {
        headers: { Authorization: `bearer ${token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch rents by lease');
    }
  }
);

export const createRent = createAsyncThunk(
  'rent/create',
  async (rentData: any, { getState, dispatch, rejectWithValue }) => {
    try {
      const state: any = getState();
      const token = state.authentification.user.token;
      
      const response = await axios.post(`${baseUrlApi}/rents`, rentData, {
        headers: { Authorization: `bearer ${token}` }
      });
      
      if (rentData.LeaseId) {
        dispatch(fetchRentByLease(rentData.LeaseId));
      }
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "L'ajout du loyer a échoué !");
    }
  }
);

export const updateRentDateend = createAsyncThunk(
  'rent/updateDateend',
  async ({ rentId, end_date }: { rentId: number; end_date: string }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state: any = getState();
      const token = state.authentification.user.token;
      const response = await axios.put(`${baseUrlApi}/rents/${rentId}`, { end_date }, {
        headers: { Authorization: `bearer ${token}` }
      });
      dispatch(fetchRents());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Modification échouée !');
    }
  }
);

export const deleteRent = createAsyncThunk(
  'rent/delete',
  async (rentId: number, { getState, dispatch, rejectWithValue }) => {
    try {
      const state: any = getState();
      const token = state.authentification.user.token;
      
      await axios.delete(`${baseUrlApi}/rents/${rentId}`, {
        headers: { Authorization: `bearer ${token}` }
      });
      
      dispatch(fetchRents());
      
      return rentId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Suppression échouée !');
    }
  }
);

const rentSlice = createSlice({
  name: 'rent',
  initialState,
  reducers: {
    clearRentsByLease: (state) => {
      state.rentsByLease = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRents.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchRents.fulfilled, (state, action) => {
        state.loading = false;
        state.rents = action.payload;
      })
      .addCase(fetchRents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      .addCase(fetchRent.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchRent.fulfilled, (state, action) => {
        state.loading = false;
        state.rent = action.payload;
      })
      .addCase(fetchRent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      .addCase(fetchRentByLease.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchRentByLease.fulfilled, (state, action) => {
        state.loading = false;
        state.rentsByLease = action.payload;
      })
      .addCase(fetchRentByLease.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      .addCase(createRent.pending, (state) => {
        state.loading = true;
      })
      .addCase(createRent.fulfilled, (state, action) => {
        state.loading = false;
        state.rents.push(action.payload);
        state.rentsByLease.push(action.payload);
      })
      .addCase(createRent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      .addCase(updateRentDateend.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateRentDateend.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.rents.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.rents[index] = action.payload;
        }
      })
      .addCase(updateRentDateend.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      .addCase(deleteRent.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteRent.fulfilled, (state, action) => {
        state.loading = false;
        state.rents = state.rents.filter(r => r.id !== action.payload);
        state.rentsByLease = state.rentsByLease.filter(r => r.id !== action.payload);
      })
      .addCase(deleteRent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearRentsByLease } = rentSlice.actions;
export default rentSlice.reducer;